import { createMatrixFromArray, applyMatrixToPoint } from './floorPlanUtils';
import { apply3DRotationTo2D } from './geometryUtils';
import {
  isFrenchDoor,
  isInswing,
  isSwingDoor,
  getSwingDoorConfig,
} from '../constants/doorTypes';

/**
 * Compute swing door geometry from pure inputs.
 * Encapsulates the shared geometry math used by rendering, hit-testing,
 * and test helpers.
 *
 * @param {Object} params
 * @param {number} params.centerX - Door center X
 * @param {number} params.centerZ - Door center Z
 * @param {number[]} params.dir - [dirX, dirZ] normalized 2D direction vector
 * @param {number} params.doorWidth - Full door width
 * @param {string} params.doorType - Door type key (e.g. 'leftInswing')
 * @param {boolean} params.wallFacesPriorityRoom - Whether wall faces priority room
 * @returns {Object} Geometry data for the swing door
 */
export const computeSwingDoorGeometry = ({
  centerX,
  centerZ,
  dir,
  doorWidth,
  doorType,
  wallFacesPriorityRoom,
}) => {
  const halfWidth = doorWidth / 2;
  const startX = centerX - dir[0] * halfWidth;
  const startZ = centerZ - dir[1] * halfWidth;
  const endX = centerX + dir[0] * halfWidth;
  const endZ = centerZ + dir[1] * halfWidth;

  const perpDir = [-dir[1], dir[0]];

  const { hingeAtStart, swingAngle } = getSwingDoorConfig(
    doorType,
    wallFacesPriorityRoom
  );

  const hingeX = hingeAtStart ? startX : endX;
  const hingeZ = hingeAtStart ? startZ : endZ;
  const oppositeX = hingeAtStart ? endX : startX;
  const oppositeZ = hingeAtStart ? endZ : startZ;

  const closedAngle = Math.atan2(oppositeZ - hingeZ, oppositeX - hingeX);
  const openAngle = closedAngle + swingAngle;

  return {
    startX,
    startZ,
    endX,
    endZ,
    perpDir,
    hingeX,
    hingeZ,
    oppositeX,
    oppositeZ,
    closedAngle,
    openAngle,
    swingAngle,
    hingeAtStart,
  };
};

/**
 * One leaf of a door, fully described: where it hinges, where it points
 * shut, and where it points open.
 */
const makeDoorLeaf = (
  hingeX,
  hingeZ,
  towardX,
  towardZ,
  leafWidth,
  swingAngle
) => {
  const closedAngle = Math.atan2(towardZ - hingeZ, towardX - hingeX);
  const openAngle = closedAngle + swingAngle;
  return {
    hingeX,
    hingeZ,
    leafWidth,
    closedAngle,
    swingAngle,
    openAngle,
    closedTipX: hingeX + Math.cos(closedAngle) * leafWidth,
    closedTipZ: hingeZ + Math.sin(closedAngle) * leafWidth,
    openTipX: hingeX + Math.cos(openAngle) * leafWidth,
    openTipZ: hingeZ + Math.sin(openAngle) * leafWidth,
  };
};

/**
 * THE description of a door's swing: its leaves.
 *
 * - a single swing door -> one leaf (exactly `computeSwingDoorGeometry`)
 * - a French pair       -> two, each hinged at its own jamb, closed
 *                          toward the centre, sweeping the same way
 * - sliding / pocket / untyped -> none; they do not swing
 *
 * Draw or reason about a swing by looping over this. It exists because
 * the pair had no shared description, so each consumer re-derived it by
 * hand and they drifted.
 *
 * Mirrors computeDoorLeaves() in functions/shared/doorTypes.js.
 *
 * @returns {Array<Object>} leaves, hinge-ordered (French: start jamb first)
 */
export const computeDoorLeaves = ({
  centerX,
  centerZ,
  dir,
  doorWidth,
  doorType,
  wallFacesPriorityRoom,
}) => {
  const halfWidth = doorWidth / 2;
  const startX = centerX - dir[0] * halfWidth;
  const startZ = centerZ - dir[1] * halfWidth;
  const endX = centerX + dir[0] * halfWidth;
  const endZ = centerZ + dir[1] * halfWidth;

  if (isFrenchDoor(doorType)) {
    // Both leaves swing the same way; which jamb is "active" changes the
    // hardware, not the geometry.
    const sweep = isInswing(doorType) ? Math.PI / 2 : -Math.PI / 2;
    return [
      makeDoorLeaf(startX, startZ, centerX, centerZ, halfWidth, sweep),
      makeDoorLeaf(endX, endZ, centerX, centerZ, halfWidth, -sweep),
    ];
  }

  if (!isSwingDoor(doorType)) return [];

  const geo = computeSwingDoorGeometry({
    centerX,
    centerZ,
    dir,
    doorWidth,
    doorType,
    wallFacesPriorityRoom,
  });
  return [
    makeDoorLeaf(
      geo.hingeX,
      geo.hingeZ,
      geo.oppositeX,
      geo.oppositeZ,
      doorWidth,
      geo.swingAngle
    ),
  ];
};

/**
 * Check if a point is near a door (new data format with center/rotation)
 */
export const isPointNearDoorV2 = (worldX, worldY, door, threshold = 0.25) => {
  if (
    !door.center ||
    door.center.x === undefined ||
    door.center.z === undefined ||
    !door.dimensions
  ) {
    return false;
  }

  const halfWidth = door.dimensions.x / 2;

  // Get door direction from rotation
  const normalizedDir = apply3DRotationTo2D(door.rotation);

  // Calculate door endpoints
  const startX = door.center.x - normalizedDir[0] * halfWidth;
  const startZ = door.center.z - normalizedDir[1] * halfWidth;
  const endX = door.center.x + normalizedDir[0] * halfWidth;
  const endZ = door.center.z + normalizedDir[1] * halfWidth;

  // First check if point is near the door line
  const dx = endX - startX;
  const dy = endZ - startZ;
  const t = Math.max(
    0,
    Math.min(
      1,
      ((worldX - startX) * dx + (worldY - startZ) * dy) / (dx * dx + dy * dy)
    )
  );
  const nearestX = startX + t * dx;
  const nearestY = startZ + t * dy;
  const distanceToLine = Math.sqrt(
    Math.pow(worldX - nearestX, 2) + Math.pow(worldY - nearestY, 2)
  );

  return distanceToLine <= threshold;
};

/**
 * Check if a point is exactly within a door's rendered bounds (door line + swing arc)
 * Used for exact hit detection in reverse render order
 */
export const isPointInDoorBounds = (
  worldX,
  worldY,
  door,
  wallThickness = 0.15
) => {
  if (!door.transform || !door.dimensions) return false;

  const matrix = createMatrixFromArray(door.transform);
  const center = applyMatrixToPoint(matrix, [0, 0, 0]);
  const dimensions = door.dimensions || [1, 2, 0];
  const doorWidth = dimensions[0];
  const halfWidth = doorWidth / 2;

  // Get door direction from matrix
  const direction = [matrix[0], matrix[2]];
  const length = Math.sqrt(
    direction[0] * direction[0] + direction[1] * direction[1]
  );
  const normalizedDir = [direction[0] / length, direction[1] / length];
  const perpDir = [-normalizedDir[1], normalizedDir[0]];

  // Calculate door endpoints
  const startX = center[0] - normalizedDir[0] * halfWidth;
  const startY = center[2] - normalizedDir[1] * halfWidth;
  const endX = center[0] + normalizedDir[0] * halfWidth;
  const endY = center[2] + normalizedDir[1] * halfWidth;

  // Check if point is within the door opening line (uses wall thickness)
  const doorOpeningWidth = Math.max(wallThickness + 0.01, 0.07);
  const dx = worldX - center[0];
  const dy = worldY - center[2];
  const alongDoor = dx * normalizedDir[0] + dy * normalizedDir[1];
  const perpToDoor = dx * perpDir[0] + dy * perpDir[1];

  if (
    Math.abs(alongDoor) <= halfWidth &&
    Math.abs(perpToDoor) <= doorOpeningWidth / 2
  ) {
    return true;
  }

  // Check if point is within the swing arc
  const doorType = door.category?.door?.doorType;
  const { hingeAtStart, swingAngle } = getSwingDoorConfig(
    doorType,
    door.wallFacesPriorityRoom
  );

  const hingeX = hingeAtStart ? startX : endX;
  const hingeY = hingeAtStart ? startY : endY;
  const oppositeX = hingeAtStart ? endX : startX;
  const oppositeY = hingeAtStart ? endY : startY;

  const distanceFromHinge = Math.sqrt(
    Math.pow(worldX - hingeX, 2) + Math.pow(worldY - hingeY, 2)
  );

  // Check if within swing radius (with small tolerance for the arc line)
  const arcTolerance = 0.03; // 3cm tolerance for the arc line
  if (
    distanceFromHinge > doorWidth + arcTolerance ||
    distanceFromHinge < doorWidth - arcTolerance
  ) {
    // Not on the arc line itself, but check if on the open door line
    if (distanceFromHinge <= doorWidth) {
      const clickAngle = Math.atan2(worldY - hingeY, worldX - hingeX);
      const closedAngle = Math.atan2(oppositeY - hingeY, oppositeX - hingeX);
      const openAngle = closedAngle + swingAngle;

      // Check if click is on the open door line (within tolerance)
      const angleDiff = Math.abs(clickAngle - openAngle);
      const normalizedDiff = Math.min(angleDiff, 2 * Math.PI - angleDiff);
      if (normalizedDiff < 0.1) {
        // ~6 degrees tolerance
        return true;
      }
    }
    return false;
  }

  // Point is on the arc line, check if within the swing angle range
  const clickAngle = Math.atan2(worldY - hingeY, worldX - hingeX);
  const closedAngle = Math.atan2(oppositeY - hingeY, oppositeX - hingeX);

  const normalizeAngle = (angle) => {
    while (angle < 0) angle += 2 * Math.PI;
    while (angle >= 2 * Math.PI) angle -= 2 * Math.PI;
    return angle;
  };

  const normClickAngle = normalizeAngle(clickAngle);
  const normClosedAngle = normalizeAngle(closedAngle);
  const endAngle = normalizeAngle(normClosedAngle + swingAngle);

  let inArc = false;
  if (swingAngle > 0) {
    if (endAngle > normClosedAngle) {
      inArc = normClickAngle >= normClosedAngle && normClickAngle <= endAngle;
    } else {
      inArc = normClickAngle >= normClosedAngle || normClickAngle <= endAngle;
    }
  } else {
    if (endAngle < normClosedAngle) {
      inArc = normClickAngle >= endAngle && normClickAngle <= normClosedAngle;
    } else {
      inArc = normClickAngle >= endAngle || normClickAngle <= normClosedAngle;
    }
  }

  return inArc;
};

/**
 * Check if a point is near a door (including within its swing area) - old format
 */
export const isPointNearDoor = (worldX, worldY, door, threshold = 0.2) => {
  if (!door.transform || !door.dimensions) return false;

  const matrix = createMatrixFromArray(door.transform);
  const center = applyMatrixToPoint(matrix, [0, 0, 0]);
  const dimensions = door.dimensions || [1, 2, 0];
  const doorWidth = dimensions[0];
  const halfWidth = doorWidth / 2;

  // Get door direction from matrix
  const direction = [matrix[0], matrix[2]];
  const length = Math.sqrt(
    direction[0] * direction[0] + direction[1] * direction[1]
  );
  const normalizedDir = [direction[0] / length, direction[1] / length];

  // Calculate door endpoints
  const startX = center[0] - normalizedDir[0] * halfWidth;
  const startY = center[2] - normalizedDir[1] * halfWidth;
  const endX = center[0] + normalizedDir[0] * halfWidth;
  const endY = center[2] + normalizedDir[1] * halfWidth;

  // First check if point is near the door line (original check)
  const dx = endX - startX;
  const dy = endY - startY;
  const t = Math.max(
    0,
    Math.min(
      1,
      ((worldX - startX) * dx + (worldY - startY) * dy) / (dx * dx + dy * dy)
    )
  );
  const nearestX = startX + t * dx;
  const nearestY = startY + t * dy;
  const distanceToLine = Math.sqrt(
    Math.pow(worldX - nearestX, 2) + Math.pow(worldY - nearestY, 2)
  );

  return distanceToLine <= threshold;
};

/**
 * Draw all doors in the floor plan
 */
export const drawDoors = (ctx, doors, walls, rooms, selectedItem, scale) => {
  doors.forEach((door) => {
    const matrix = createMatrixFromArray(door.transform);
    const dimensions = door.dimensions || [1, 2, 0];
    const doorWidth = dimensions[0];

    // Get door center in world space
    const center = applyMatrixToPoint(matrix, [0, 0, 0]);

    // Door properties
    const halfWidth = doorWidth / 2;

    // Get door direction from matrix
    const direction = [matrix[0], matrix[2]]; // X and Z components of first column
    const length = Math.sqrt(
      direction[0] * direction[0] + direction[1] * direction[1]
    );
    const normalizedDir = [direction[0] / length, direction[1] / length];

    // Get perpendicular direction (for door thickness and swing)
    const perpDir = [-normalizedDir[1], normalizedDir[0]];

    // Calculate the door position
    const startX = center[0] - normalizedDir[0] * halfWidth;
    const startZ = center[2] - normalizedDir[1] * halfWidth;
    const endX = center[0] + normalizedDir[0] * halfWidth;
    const endZ = center[2] + normalizedDir[1] * halfWidth;

    // Check if this door is selected
    const isSelected = selectedItem && selectedItem.id === door.id;

    // Determine hinge side and swing direction from doorType
    const doorType = door.category?.door?.doorType;
    const { hingeAtStart, swingAngle } = getSwingDoorConfig(
      doorType,
      door.wallFacesPriorityRoom
    );

    // Find the wall thickness by checking nearby walls
    let wallThickness = 0.15; // Default thickness (matching drawWallsFromRoomPolygon)

    if (walls && walls.length > 0) {
      // Find the closest wall to this door
      let closestWall = null;
      let minDistance = Infinity;

      walls.forEach((wall) => {
        const wallMatrix = createMatrixFromArray(wall.transform);
        const wallCenter = applyMatrixToPoint(wallMatrix, [0, 0, 0]);

        // Simple distance check to door center
        const distance = Math.sqrt(
          Math.pow(center[0] - wallCenter[0], 2) +
            Math.pow(center[2] - wallCenter[2], 2)
        );

        if (distance < minDistance && distance < 2.0) {
          // Within 2 meters
          minDistance = distance;
          closestWall = wall;
        }
      });

      if (
        closestWall &&
        closestWall.dimensions &&
        closestWall.dimensions[2] > 0
      ) {
        wallThickness = closestWall.dimensions[2];
      } else if (closestWall) {
        wallThickness = 0.06; // Default wall thickness from drawWalls
      }
    }

    // Scale-aware line width based on wall thickness
    const doorOpeningWidth = Math.max(wallThickness + 0.01, 0.06 + 0.01); // At least 6cm, 1cm buffer

    // Draw door opening as white bar with wall thickness
    ctx.save();
    ctx.strokeStyle = isSelected ? '#ffcccc' : 'white';
    ctx.lineWidth = doorOpeningWidth;
    ctx.lineCap = 'butt';
    ctx.beginPath();
    ctx.moveTo(startX, startZ);
    ctx.lineTo(endX, endZ);
    ctx.stroke();
    ctx.restore();

    // Draw door hinge (small circle)
    const hingeX = hingeAtStart ? startX : endX;
    const hingeZ = hingeAtStart ? startZ : endZ;
    const oppositeX = hingeAtStart ? endX : startX;
    const oppositeZ = hingeAtStart ? endZ : startZ;

    ctx.beginPath();
    ctx.arc(hingeX, hingeZ, Math.max(0.01, 3 / scale), 0, Math.PI * 2);
    ctx.fillStyle = isSelected ? '#ff6666' : '#666666';
    ctx.fill();

    // Always draw swing arc to show direction
    const arcRadius = doorWidth;

    // Calculate the angle of the door in closed position
    const closedAngle = Math.atan2(oppositeZ - hingeZ, oppositeX - hingeX);

    // Calculate swing arc angles based on swingAngle from config
    const startAngle = closedAngle;
    const endAngle = closedAngle + swingAngle;
    const openAngle = closedAngle + swingAngle;

    // Draw the arc
    ctx.beginPath();
    ctx.arc(
      hingeX,
      hingeZ,
      arcRadius,
      startAngle,
      endAngle,
      swingAngle < 0 // For outward swing (negative angle), go counter-clockwise
    );
    ctx.lineWidth = 0.02; // Always 0.02 meters (2cm)
    ctx.setLineDash([Math.max(0.02, 5 / scale), Math.max(0.02, 5 / scale)]);
    ctx.strokeStyle = isSelected
      ? 'rgba(255, 102, 102, 0.6)'
      : 'rgba(136, 68, 68, 0.6)';
    ctx.stroke();
    ctx.setLineDash([]);

    // Calculate the end position of the door when open
    const openDoorX = hingeX + Math.cos(openAngle) * doorWidth;
    const openDoorZ = hingeZ + Math.sin(openAngle) * doorWidth;

    // Draw the door in open position (perpendicular bar at end of arc)
    ctx.beginPath();
    ctx.moveTo(hingeX, hingeZ);
    ctx.lineTo(openDoorX, openDoorZ);
    ctx.lineWidth = Math.max(0.02, 3 / scale);
    ctx.strokeStyle = isSelected ? '#ff6666' : '#666666';
    ctx.stroke();
  });
};
